import { useEffect, useRef } from 'react'

/**
 * Cloudflare Turnstile widget — SMS-pumping / toll-fraud defense for the portal
 * sign-in's phone send-code step (the same abuse the API's per-IP rate limiter
 * can't fully catch from a rotating-IP botnet; see parkify-pay/api/src/utils/
 * turnstile.ts + docs/WAF-EDGE-HARDENING.md). Mirrors the consumer checkout's
 * Turnstile.tsx — kept app-local (each app bakes its own VITE_TURNSTILE_SITEKEY)
 * rather than shared, since the component is tiny and self-contained.
 *
 * DARK BY DEFAULT: with no VITE_TURNSTILE_SITEKEY the component renders nothing
 * and never emits a token, so the build ships unchanged until the sitekey is
 * baked in.
 *
 * ENFORCEMENT IS LIVE: the API now verifies the token on /phone/start behind
 * TURNSTILE_PHONE_ENABLED (flipped to "true" — see parkify-pay/api/wrangler.jsonc
 * + docs/WAF-EDGE-HARDENING.md). So a flagged visitor who submits WITHOUT solving
 * the challenge is rejected server-side. To keep that from silently breaking the
 * mobile front door, the caller uses `onStatus` to gate its submit button while
 * an interactive challenge is pending — and to fail OPEN (never block) on a
 * script/verify error, so a genuine user is never stuck behind a broken widget.
 *
 * Rendered `interaction-only` — the most-invisible CLIENT setting: genuine users
 * self-complete silently in ~1s, no checkbox, no friction on the sign-in door.
 *
 * INVISIBILITY IS SET BY THE SITEKEY'S WIDGET TYPE, NOT HERE. `interaction-only`
 * cannot suppress a challenge on a **Managed** sitekey — a Managed key still shows
 * a visible challenge whenever Cloudflare flags the visitor, which is what made it
 * "pop up" on the send-code screen. The sitekey MUST be an **Invisible** widget
 * type to stay silent for everyone (see docs/WAF-EDGE-HARDENING.md). The onStatus
 * submit-gating below only matters for a Managed key (an Invisible widget never
 * fires before-interactive-callback); it's harmless — and dormant — on an
 * Invisible key, and still fails open on a script/verify error.
 */

const SITEKEY = import.meta.env.VITE_TURNSTILE_SITEKEY as string | undefined
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'

interface TurnstileApi {
  render: (el: HTMLElement, opts: Record<string, unknown>) => string
  remove: (id: string) => void
  reset: (id: string) => void
}
declare global {
  interface Window {
    turnstile?: TurnstileApi
  }
}

let scriptPromise: Promise<void> | null = null
function loadScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.reject(new Error('no window'))
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script')
    s.src = SCRIPT_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => {
      scriptPromise = null // allow a later retry
      reject(new Error('turnstile script failed to load'))
    }
    document.head.appendChild(s)
  })
  return scriptPromise
}

/** True when the build is configured to render Turnstile (a sitekey is baked in). */
export function isTurnstileConfigured(): boolean {
  return !!SITEKEY
}

/**
 * Widget lifecycle, surfaced so the caller can gate submit:
 *   'interactive' — Cloudflare is showing a challenge and is WAITING on the user
 *                   (a real token isn't available yet → caller should block send)
 *   'solved'      — a token was emitted (via onToken); safe to submit
 *   'expired'     — a prior token lapsed; onToken('') already fired
 *   'error'       — script blocked / verify errored → caller should FAIL OPEN
 */
export type TurnstileStatus = 'interactive' | 'solved' | 'expired' | 'error'

interface Props {
  /** Receives the token on solve, and '' on expiry/error (single-use upstream). */
  onToken: (token: string) => void
  /** Lifecycle signal for submit-gating (see TurnstileStatus). Optional so
   *  existing callers that don't gate keep working unchanged. */
  onStatus?: (status: TurnstileStatus) => void
  /** Bump to force a fresh widget + token (e.g. after a token was consumed). */
  refreshKey?: number
}

export default function Turnstile({ onToken, onStatus, refreshKey = 0 }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)

  useEffect(() => {
    if (!SITEKEY) return
    let cancelled = false

    const removeWidget = () => {
      if (widgetId.current && window.turnstile) {
        try { window.turnstile.remove(widgetId.current) } catch { /* already gone */ }
      }
      widgetId.current = null
    }

    loadScript()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return
        removeWidget() // clear any prior render (refreshKey bump)
        widgetId.current = window.turnstile.render(ref.current, {
          sitekey: SITEKEY,
          // Invisible for genuine users; only renders a challenge if Cloudflare
          // flags the visitor (see header note). No checkbox on the front door.
          appearance: 'interaction-only',
          callback: (token: string) => { onToken(token); onStatus?.('solved') },
          // Fires when an interactive challenge is about to be shown — the signal
          // the caller uses to hold submit until the visitor actually solves it.
          'before-interactive-callback': () => onStatus?.('interactive'),
          'expired-callback': () => { onToken(''); onStatus?.('expired') },
          'error-callback': () => { onToken(''); onStatus?.('error') },
          // An interactive challenge that times out before the visitor solves it
          // fires this (NOT expired-callback). Drop the blocking status so the
          // submit button can't get stuck disabled — fail open. Cloudflare
          // auto-refreshes the challenge (refresh-timeout defaults to 'auto'),
          // which re-fires before-interactive-callback and re-gates if a fresh
          // solve is still needed.
          'timeout-callback': () => { onToken(''); onStatus?.('expired') },
        })
      })
      .catch(() => {
        // Script blocked/unreachable → no token emitted, and no challenge can
        // ever render. Signal 'error' so the caller FAILS OPEN (a genuine
        // sign-in still goes through); only the bot defense is lost.
        onStatus?.('error')
      })

    return () => {
      cancelled = true
      removeWidget()
    }
  }, [onToken, onStatus, refreshKey])

  if (!SITEKEY) return null
  // Invisible until Cloudflare flags the visitor; the margin only matters if an
  // interactive challenge actually renders here, above the submit button.
  return <div ref={ref} style={{ display: 'flex', justifyContent: 'center', margin: '4px 0' }} />
}
